// The group photo: read from the phone and shrunk before it goes up (the server finds the faces,
// src/yunet.ts), and each face cropped for avatars and contact cards.

const MAX_SIDE = 1280 // ~150–250 KB as JPEG, so it goes up quickly on a weak signal

const canvasOf = (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h })

// Decoding through <img> applies the photo's EXIF rotation everywhere (createImageBitmap doesn't
// on every Safari).
export async function loadImage(blob) {
  const url = URL.createObjectURL(blob)
  const img = new Image()
  img.src = url
  try {
    await img.decode()
  } catch (e) {
    URL.revokeObjectURL(url)
    throw new Error("Couldn't read that photo")
  }
  return { img, url, w: img.naturalWidth, h: img.naturalHeight }
}

export async function readPhoto(file) {
  const src = await loadImage(file)
  const scale = Math.min(1, MAX_SIDE / Math.max(src.w, src.h))
  const w = Math.round(src.w * scale), h = Math.round(src.h * scale)
  const canvas = canvasOf(w, h)
  canvas.getContext('2d').drawImage(src.img, 0, 0, w, h)
  URL.revokeObjectURL(src.url)
  const jpeg = await new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("Couldn't read that photo"))), 'image/jpeg', 0.82))
  return { canvas, jpeg, w, h }
}

// A square around the face with room for hair and chin, `size` px across.
export function cropFace(img, [fx, fy, fw, fh], size) {
  const W = img.naturalWidth ?? img.width, H = img.naturalHeight ?? img.height
  const side = Math.min(W, H, Math.max(fw * W, fh * H) * 1.4)
  const cx = (fx + fw / 2) * W, cy = (fy + fh / 2) * H
  const sx = Math.min(Math.max(0, cx - side / 2), W - side)
  const sy = Math.min(Math.max(0, cy - side / 2), H - side)
  const c = canvasOf(size, size)
  c.getContext('2d').drawImage(img, sx, sy, side, side, 0, 0, size, size)
  return c
}

export const canvasJpeg = (canvas, q = 0.85) =>
  new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('crop failed'))), 'image/jpeg', q))

// Base64 JPEG of a face, for a contact card's PHOTO.
export async function faceBase64(img, box, size = 256) {
  const bytes = new Uint8Array(await (await canvasJpeg(cropFace(img, box, size))).arrayBuffer())
  let s = ''
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(s)
}
